import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, SmartphoneIcon, GlobeIcon, PhoneIcon, MailIcon, MessageSquareIcon, BrainCircuitIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
// FR lexicon (locked) for the Requests / Requêtes BCITI page. EN untouched.
// Single map for diagram channels, the comparison columns, the intelligence
// diagram nodes and the industries matrix (headers + items). "ATNA",
// "Infrastructure" and "Action" are intentionally kept as-is.
const FR: Record<string, string> = {
  // Hero diagram — channels
  'Mobile App': 'Appli mobile',
  Website: 'Site web',
  Phone: 'Téléphone',
  Email: 'Courriel',
  'Social Media': 'Réseaux sociaux',
  // Hero diagram — bottom steps
  Prioritize: 'Prioriser',
  Assign: 'Assigner',
  Resolve: 'Résoudre',
  Analyze: 'Analyser',
  // Comparison — traditional column
  'Scattered Channels': 'Canaux dispersés',
  'Manual Triage': 'Tri manuel',
  'No Visibility': 'Aucune visibilité',
  'Lost Requests': 'Requêtes perdues',
  'Department Guesswork': 'Devinettes entre services',
  'Disconnected Reporting': 'Rapports déconnectés',
  // Comparison — BCITI column
  'Single Operational Intake': "Point d'entrée opérationnel unique",
  'SLA-Based Prioritization': 'Priorisation basée sur les SLA',
  'Complete Tracking': 'Suivi complet',
  'Closed-Loop Resolution': 'Résolution en boucle fermée',
  'Smart Assignment': 'Assignation intelligente',
  'Operational Analytics': 'Analytique opérationnelle',
  // Brossard proof attributes
  'Since 2017': 'Depuis 2017',
  'Live in Production': 'En production active',
  'Verified Identity Infrastructure': "Infrastructure d'identité vérifiée",
  'Closed-Loop Communications': 'Communications en boucle fermée',
  'Law 25 Ready': 'Conforme Loi 25',
  'Canadian Hosted Infrastructure': 'Infrastructure hébergée au Canada',
  // Intelligence diagram nodes
  Requests: 'Requêtes',
  'Operational Data': 'Données opérationnelles',
  'ATNA Insights': 'Analyses ATNA',
  'Verified Recommendations': 'Recommandations vérifiées',
  // Industries matrix — headers
  Cities: 'Villes',
  Universities: 'Universités',
  Airports: 'Aéroports',
  'Retail & Telecom': 'Commerce & Télécom',
  Healthcare: 'Santé',
  // Industries matrix — items
  Potholes: 'Nids-de-poule',
  Permits: 'Permis',
  '311 Requests': 'Requêtes 311',
  'Student Services': 'Services aux étudiants',
  'IT Support': 'Soutien TI',
  Facilities: 'Installations',
  'Campus Requests': 'Requêtes du campus',
  'Lost Property': 'Objets perdus',
  'Passenger Complaints': 'Plaintes des passagers',
  'Access Requests': "Demandes d'accès",
  Operations: 'Opérations',
  'Customer Support': 'Soutien à la clientèle',
  'Service Requests': 'Demandes de service',
  Claims: 'Réclamations',
  Escalations: 'Escalades',
  'Patient Requests': 'Demandes des patients',
  'Facility Issues': "Problèmes d'installation",
  'Service Coordination': 'Coordination des services',
  'Support Requests': 'Demandes de soutien'
};
// Workflow steps (title + description) for "From intake to resolution."
const STEP_FR: Record<string, {
  title: string;
  desc: string;
}> = {
  Intake: {
    title: 'Réception',
    desc: '(Appli, portail, téléphone, courriel, réseaux, ATNA)'
  },
  'Verified Identity': {
    title: 'Identité vérifiée',
    desc: 'Savoir qui fait la demande'
  },
  'SLA Prioritization': {
    title: 'Priorisation SLA',
    desc: 'Priorité selon vos règles'
  },
  'Smart Assignment': {
    title: 'Assignation intelligente',
    desc: 'Propulsé par ATNA. Achemine vers le bon service, la bonne équipe ou le bon opérateur.'
  },
  Resolution: {
    title: 'Résolution',
    desc: 'Collaboration interne et exécution des tâches'
  },
  'Closed Loop Communication': {
    title: 'Communication en boucle fermée',
    desc: 'Mises à jour automatiques au demandeur'
  },
  Analytics: {
    title: 'Analytique',
    desc: 'Intelligence opérationnelle'
  }
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  traditional: 'Scattered Channels',
  bciti: 'Single Operational Intake'
}, {
  traditional: 'Manual Triage',
  bciti: 'SLA-Based Prioritization'
}, {
  traditional: 'No Visibility',
  bciti: 'Complete Tracking'
}, {
  traditional: 'Lost Requests',
  bciti: 'Closed-Loop Resolution'
}, {
  traditional: 'Department Guesswork',
  bciti: 'Smart Assignment'
}, {
  traditional: 'Disconnected Reporting',
  bciti: 'Operational Analytics'
}];
const MATRIX_COLUMNS = [{
  title: 'Cities',
  items: ['Potholes', 'Permits', 'Infrastructure', '311 Requests']
}, {
  title: 'Universities',
  items: ['Student Services', 'IT Support', 'Facilities', 'Campus Requests']
}, {
  title: 'Airports',
  items: ['Lost Property', 'Passenger Complaints', 'Access Requests', 'Operations']
}, {
  title: 'Retail & Telecom',
  items: ['Customer Support', 'Service Requests', 'Claims', 'Escalations']
}, {
  title: 'Healthcare',
  items: ['Patient Requests', 'Facility Issues', 'Service Coordination', 'Support Requests']
}];
export function Requests() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  const stepTitle = (en: string) => lang === 'FR' ? STEP_FR[en]?.title ?? en : en;
  const stepDesc = (en: string, desc: string) => lang === 'FR' ? STEP_FR[en]?.desc ?? desc : desc;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Requests'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2505">
      <Header data-id="element-2506" />

      <main className="pt-[120px]" data-id="element-2507">
        {/* HERO SECTION */}
        <section className="pt-24 pb-32 px-6" data-id="element-2508">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2509">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-2510">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-indigo uppercase mb-6" data-id="element-2511">
                {lang === 'FR' ? 'Requêtes BCITI' : 'BCITI Requests'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-2512">
                {lang === 'FR' ? 'Tous les canaux. Une seule requête.' : 'Every Channel In. One Request.'}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-6" data-id="element-2513">
                {lang === 'FR' ? 'Appli, portail web, téléphone, courriel, réseaux sociaux ou ATNA.' : 'App, web portal, phone, email, social media, or ATNA.'}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-2514">
                {lang === 'FR' ? 'Chaque requête entrante devient une action priorisée, assignée, suivie et résolue. Rien ne se perd.' : 'Every incoming request becomes a prioritized, assigned, tracked, and resolved action. Nothing gets lost.'}
              </p>
              <div className="flex flex-wrap items-center gap-6" data-id="element-2515">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Requests Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-2516">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2517" />
                </Link>
                <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#121212] hover:text-indigo transition-colors" data-id="element-2518">
                  {lang === 'FR' ? 'Découvrir Brossard' : 'Explore Brossard'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2519" />
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative flex flex-col items-center" data-id="element-2520">
              <img src={lang === 'FR' ? 'https://cdn.magicpatterns.com/uploads/9y2vptsgq48L6TXNU122pK/ChatGPT_Image_7_juil._2026,_14_05_10.png' : 'https://cdn.magicpatterns.com/uploads/w9EdBPEpEigqsAkGjG72To/ChatGPT_Image_7_juil._2026,_14_08_32.png'} alt={lang === 'FR' ? 'Tous les canaux (application mobile, site web, téléphone, courriel, réseaux sociaux, ATNA) convergent vers Requêtes BCITI, puis Prioriser, Assigner, Résoudre, Analyser' : 'Every channel (mobile app, website, phone, email, social media, ATNA) converges into BCITI Requests, then Prioritize, Assign, Resolve, Analyze'} className="w-full h-auto object-contain max-w-md mx-auto" data-id="element-2521" />
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: THE OPERATIONAL PROBLEM */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2522">
          <div className="max-w-4xl mx-auto" data-id="element-2523">
            <div className="text-center mb-20" data-id="element-2524">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-2525">
                {lang === 'FR' ? 'La plupart des organisations gèrent les requêtes en silos.' : 'Most organizations manage requests in silos.'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60" data-id="element-2526">
                {lang === 'FR' ? 'Les équipes des opérations passent leur temps à chercher, acheminer et relancer au lieu de résoudre les problèmes.' : 'Operations teams spend time searching, routing, and following up instead of solving issues.'}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-8" data-id="element-2527">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2528">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-2529">
                  {lang === 'FR' ? 'Approche traditionnelle' : 'Traditional Approach'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-indigo uppercase" data-id="element-2530">
                  {lang === 'FR' ? 'Requêtes BCITI' : 'BCITI Requests'}
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-2531">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-2532">
                    {t(row.traditional)}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-2533">
                    {t(row.bciti)}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center" data-id="element-2534">
              <p className="font-sans text-sm text-[#121212]/50 tracking-wide" data-id="element-2535">
                {lang === 'FR' ? "Un seul flux opérationnel, quelle que soit l'origine de la requête." : 'One operational workflow regardless of where the request originates.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: HOW IT WORKS */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2536">
          <div className="max-w-6xl mx-auto" data-id="element-2537">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-2538">
              {lang === 'FR' ? 'De la réception à la résolution.' : 'From intake to resolution.'}
            </motion.h2>

            <div className="flex flex-col lg:flex-row items-start justify-between gap-6 relative" data-id="element-2539">
              {/* Desktop connecting line */}
              <div className="hidden lg:block absolute top-[42px] left-0 right-0 h-px bg-[#E4E6EC] z-0" data-id="element-2540" />

              {[{
              num: '1',
              title: 'Intake',
              desc: '(App, Portal, Phone, Email, Social, ATNA)'
            }, {
              num: '2',
              title: 'Verified Identity',
              desc: 'Know who is requesting'
            }, {
              num: '3',
              title: 'SLA Prioritization',
              desc: 'Priority based on your rules'
            }, {
              num: '4',
              title: 'Smart Assignment',
              desc: 'Powered by ATNA. Route to the right department, team, or operator.',
              highlight: true
            }, {
              num: '5',
              title: 'Resolution',
              desc: 'Internal collaboration and task execution'
            }, {
              num: '6',
              title: 'Closed Loop Communication',
              desc: 'Automatic updates to requester'
            }, {
              num: '7',
              title: 'Analytics',
              desc: 'Operational intelligence'
            }].map((step, i) => <motion.div key={step.num} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="relative z-10 flex flex-col items-center text-center w-full lg:w-32" data-id="element-2541">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-serif text-xl mb-6 border ${step.highlight ? 'bg-indigo text-white border-indigo' : 'bg-white text-[#121212] border-[#E4E6EC]'}`} data-id="element-2542">
                    {step.num}
                  </div>
                  <h3 className={`font-serif text-lg mb-2 ${step.highlight ? 'text-indigo' : 'text-[#121212]'}`} data-id="element-2543">
                    {stepTitle(step.title)}
                  </h3>
                  <p className="font-sans text-xs text-[#121212]/60 leading-relaxed" data-id="element-2544">
                    {stepDesc(step.title, step.desc)}
                  </p>

                  {/* Mobile connecting arrow */}
                  {i < 6 && <ArrowDownIcon className="w-5 h-5 text-[#E4E6EC] my-6 lg:hidden" data-id="element-2545" />}
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 4: EVERY REQUEST CREATES INTELLIGENCE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2546">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2547">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-2548">
              {lang === 'FR' ? 'Chaque requête résolue rend la plateforme plus intelligente.' : 'Every resolved request makes the platform smarter.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-20" data-id="element-2549">
              {lang === 'FR' ? 'Les requêtes génèrent une intelligence opérationnelle par sujet, lieu, service et période.' : 'Requests generate operational intelligence by topic, location, department, and time.'}
            </motion.p>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col md:flex-row items-center justify-center gap-4 mb-20" data-id="element-2550">
              {['Requests', 'Operational Data', 'ATNA Insights', 'Verified Recommendations', 'Action'].map((node, i, arr) => <Fragment key={node} data-id="element-2551">
                  <div className={`px-6 py-4 border ${node.includes('ATNA') ? 'border-indigo bg-indigo/5 text-indigo font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'} font-sans text-sm`} data-id="element-2552">
                    {t(node)}
                  </div>
                  {i < arr.length - 1 && <ArrowRightIcon className="w-5 h-5 text-[#E4E6EC] rotate-90 md:rotate-0" data-id="element-2553" />}
                </Fragment>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="space-y-4 font-sans text-lg text-[#121212]/70" data-id="element-2554">
              <p data-id="element-2555">
                {lang === 'FR' ? "Une hausse des demandes d'entretien routier." : 'A surge in road maintenance requests.'}
              </p>
              <p data-id="element-2556">
                {lang === 'FR' ? 'Une augmentation des plaintes de stationnement.' : 'An increase in parking complaints.'}
              </p>
              <p data-id="element-2557">
                {lang === 'FR' ? "Une concentration de déclarations d'objets perdus." : 'A concentration of lost-property reports.'}
              </p>
              <p className="text-[#121212] font-medium mt-8" data-id="element-2558">
                {lang === 'FR' ? 'Les tendances deviennent visibles. Les décisions deviennent mesurables.' : 'Patterns become visible. Decisions become measurable.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: ONE ARCHITECTURE. MULTIPLE INDUSTRIES. */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2559">
          <div className="max-w-6xl mx-auto" data-id="element-2560">
            <div className="text-center mb-20" data-id="element-2561">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-4" data-id="element-2562">
                {lang === 'FR' ? 'Le flux ne change jamais.' : 'The workflow never changes.'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60" data-id="element-2563">
                {lang === 'FR' ? 'Seul le type de requête change.' : 'Only the request type changes.'}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-16" data-id="element-2564">
              {MATRIX_COLUMNS.map((col, i) => <div key={col.title} className="border border-[#E4E6EC] bg-white" data-id="element-2565">
                  <div className="p-6 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2566">
                    <h3 className="font-serif text-xl text-[#121212]" data-id="element-2567">
                      {t(col.title)}
                    </h3>
                  </div>
                  <div className="p-6 space-y-4" data-id="element-2568">
                    {col.items.map(item => <div key={item} className="font-sans text-sm text-[#121212]/70" data-id="element-2569">
                        {t(item)}
                      </div>)}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center" data-id="element-2570">
              <p className="font-sans text-sm tracking-widest text-[#121212]/40 uppercase" data-id="element-2571">
                {lang === 'FR' ? 'Un seul moteur. Toute requête. Toute organisation.' : 'One engine. Any request. Any organization.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: PROVEN IN PRODUCTION */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2572">
          <div className="max-w-4xl mx-auto" data-id="element-2573">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-2574">
              {lang === 'FR' ? 'Bâtie pour les opérations réelles.' : 'Built for real operations.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="bg-white border border-indigo/20 p-12 relative shadow-sm" data-id="element-2575">
              <div className="absolute -top-4 left-12 bg-indigo text-white font-sans text-xs font-bold tracking-widest uppercase px-4 py-2" data-id="element-2576">
                {lang === 'FR' ? 'En production active' : 'Live in Production'}
              </div>

              <div className="mb-12" data-id="element-2577">
                <h3 className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2578">
                  {lang === 'FR' ? 'Ville de Brossard' : 'City of Brossard'}
                </h3>
                <p className="font-sans text-[#121212]/60" data-id="element-2579">
                  {lang === 'FR' ? 'Infrastructure opérationnelle à laquelle de vraies organisations font confiance chaque jour.' : 'Operational infrastructure trusted daily by real organizations.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-y-6 gap-x-12 mb-12" data-id="element-2580">
                {['Since 2017', 'Live in Production', 'Verified Identity Infrastructure', 'Closed-Loop Communications', 'Operational Analytics', 'Law 25 Ready', 'Canadian Hosted Infrastructure'].map(item => <div key={item} className="flex items-center gap-3" data-id="element-2581">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo" data-id="element-2582" />
                    <span className="font-sans text-base text-[#121212]" data-id="element-2583">
                      {t(item)}
                    </span>
                  </div>)}
              </div>

              <div className="pt-8 border-t border-[#E4E6EC]" data-id="element-2584">
                <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-sm font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-2585">
                  {lang === 'FR' ? "Découvrir l'étude de cas Brossard" : 'Explore the Brossard Case Study'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2586" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 7: THE COST OF FRAGMENTATION */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2587">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2588">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] mb-10 leading-tight" data-id="element-2589">
              {lang === 'FR' ? <>
                  Le problème n'est pas le volume.
                  <br data-id="element-2590" />
                  Le problème, c'est la fragmentation.
                </> : <>
                  The problem is not volume.
                  <br data-id="element-2591" />
                  The problem is fragmentation.
                </>}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-6 font-sans text-lg text-[#121212]/70 leading-relaxed" data-id="element-2592">
              <p data-id="element-2593">
                {lang === 'FR' ? 'Lorsque les requêtes arrivent par des canaux déconnectés, les organisations perdent en visibilité, en responsabilité et en qualité de réponse.' : 'When requests arrive through disconnected channels, organizations lose visibility, accountability, and response quality.'}
              </p>
              <p data-id="element-2594">
                {lang === 'FR' ? "Les organisations qui bâtissent leurs fondations opérationnelles aujourd'hui se créent un avantage durable pour demain." : 'The organizations building operational foundations today create a long-term advantage tomorrow.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2595">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2596">
            <motion.h2 {...reveal} className="font-serif text-5xl text-[#121212] mb-8 leading-tight" data-id="element-2597">
              {lang === 'FR' ? <>
                  Chaque requête compte.
                  <br data-id="element-2598" />
                  Assurez-vous qu'aucune ne se perde.
                </> : <>
                  Every request matters.
                  <br data-id="element-2599" />
                  Make sure none are lost.
                </>}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="mb-12 space-y-2" data-id="element-2600">
              <p className="font-sans text-xl text-[#121212]/60" data-id="element-2601">
                {lang === 'FR' ? "Un seul point d'entrée." : 'One intake layer.'}
              </p>
              <p className="font-sans text-xl text-[#121212]/60" data-id="element-2602">
                {lang === 'FR' ? 'Un seul flux opérationnel.' : 'One operational workflow.'}
              </p>
              <p className="font-sans text-xl text-[#121212] font-medium" data-id="element-2603">
                {lang === 'FR' ? 'Un seul dossier vérifié.' : 'One verified record.'}
              </p>
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-2604">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Requests Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-2605">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2606" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2607" />
    </div>;
}